import * as React from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { DashboardController } from "../../../controllers";
import PageLoader from "../../Page/PageLoader";
import Panel from "../../Panel/Panel";
import ResourceTable from "../../Resource/ResourceTable";
import ResourceTableRow from "../../Resource/ResourceTableRow";
import ResourceTableRowData from "../../Resource/ResourceTableRowData";
import styled from "styled-components";
import TableWidgetWrapper from "./TableWidgetWrapper";
import NumberFormatter from "../../../utilities/NumberFormatter";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const Container = styled.div`
  height: 100%;
`

const TitleWithTooltip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

interface VatDataRow {
  label: string
  years: number[]
}

interface IProps {
  currency: string;
  numberFormat: string;
}

const VatWidget = (props: IProps) => {
  const { currency, numberFormat } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [response, setResponse] = React.useState<{ name: string; quarters: { net: number; vat: number }[] }[]>([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await DashboardController.getQuarterSummary();
      setResponse(response);
    } catch (error) {
      console.error("Failed to fetch VAT summary:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const title = (
    <TitleWithTooltip>
      {t('VatWidget::VAT owed')}
      <Tooltip>
        <TooltipTrigger>
          <Info className="size-3.5 text-muted-foreground" />
        </TooltipTrigger>
        <TooltipContent>
          {t('VatWidget::This VAT is not yours — set it aside so you can pay it to the government when due.')}
        </TooltipContent>
      </Tooltip>
    </TitleWithTooltip>
  );

  if (isLoading) {
    return (
      <Panel className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto" title={title}>
        <PageLoader />
      </Panel>
    );
  }

  const years = response.map((fy) => fy.name)
  const data: VatDataRow[] = [0, 1, 2, 3].map((qi) => ({
    label: t('VatWidget::Quarter {{number}}', { number: qi + 1 }),
    years: response.map((fy) => fy.quarters[qi]?.vat ?? 0),
  }));

  return (
    <Container>
      <Panel className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto" title={title}>
        <TableWidgetWrapper>
          <ResourceTable
            data={data}
            headers={[
              { title: t('VatWidget::Quarter') },
              ...years.map(name => ({ title: name }))
            ]}
            renderRow={(item: VatDataRow) => {
              return (
                <ResourceTableRow>
                  <ResourceTableRowData>
                    {item.label}
                  </ResourceTableRowData>
                  {item?.years?.map(year => (
                    <ResourceTableRowData>
                      {NumberFormatter.formatCurrency(currency, numberFormat, year)}
                    </ResourceTableRowData>
                  ))}
                </ResourceTableRow>
              )
            }}
          />
        </TableWidgetWrapper>
      </Panel>
    </Container>
  )
}

export default VatWidget
